import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type KeyedMutator } from 'swr';

import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useOrganizationReconnectRecovery } from '@apps/web/hooks/use_organization_reconnect_recovery';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { getApiClient } from '@apps/web/lib/api_client';
import { useMode } from '@apps/web/lib/mode_store';
import { organizationEventReferencesResource } from '@apps/web/lib/organization_event_resources';
import {
    useActivityEventCounts,
    useCalendarShiftsInRange,
    useClientContactAttempts,
    useContactAttempts,
    useOrganizationConversation,
    useOrganizationConversations,
    useOrganizationPendingMessages,
    useOrganizationPendingMessagesCount,
    useOrganizationUnreadCount,
    useOutreachRelatedInboundCalls,
    useShifts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ApiResponseType,
    type ContactAttempt,
    type StagedMessage,
    type StagedMessageDomain,
} from '@libraries/typescript/api_client';

const REALTIME_THROTTLE_DELAY_MS = 15_000;
const CONVERSATIONS_LIST_STALENESS_FLOOR_MS = 60_000;
const ACTIVITY_UNREAD_COUNT_REALTIME_THROTTLE_DELAY_MS = 15_000;
const CONTACT_ATTEMPTS_THROTTLE_DELAY = 5_000;
const ACTIVITY_EVENT_COUNTS_PATH =
    '/organizations/{organization_id}/activity-events/counts';
const ACTIVITY_UNREAD_COUNT_BROADCAST_CHANNEL = 'phoebe.activity-unread-counts';
const ACTIVITY_UNREAD_COUNT_REFETCH_STORAGE_PREFIX =
    'phoebe.activity-unread-counts.last-refetch:';

type ActivityEventCountsResponse = ApiResponseType<
    typeof ACTIVITY_EVENT_COUNTS_PATH,
    'get'
>;

type ActivityUnreadCountBroadcastMessage = {
    readonly type: 'activity-unread-counts-refreshed';
    readonly scopeKey: string;
    readonly counts: ActivityEventCountsResponse;
};

type ActivityUnreadCountRealtimeScope = {
    readonly organizationIdOverride?: string | null;
    readonly officeIds?: ReadonlyArray<string>;
    readonly organizationFilterIds?: ReadonlyArray<string>;
    readonly careCoordinatorIds?: ReadonlyArray<string>;
    readonly refreshIntervalMs?: number | null;
    readonly revalidateOnFocus?: boolean;
};

const activityUnreadCountLastRefetchByScope = new Map<string, number>();

function normalizeScopeIds(
    ids: ReadonlyArray<string> | undefined,
): Array<string> | undefined {
    if (!ids || ids.length === 0) {
        return undefined;
    }
    return [...ids].sort((a, b) => a.localeCompare(b));
}

function scopeIdsKey(ids: ReadonlyArray<string> | undefined): string {
    return ids && ids.length > 0 ? ids.join(',') : '*';
}

function isActivityUnreadCountBroadcastMessage(
    value: unknown,
): value is ActivityUnreadCountBroadcastMessage {
    return (
        typeof value === 'object' &&
        value !== null &&
        (value as ActivityUnreadCountBroadcastMessage).type ===
            'activity-unread-counts-refreshed' &&
        typeof (value as ActivityUnreadCountBroadcastMessage).scopeKey ===
            'string' &&
        typeof (value as ActivityUnreadCountBroadcastMessage).counts ===
            'object' &&
        (value as ActivityUnreadCountBroadcastMessage).counts !== null
    );
}

function claimActivityUnreadCountRefetch(scopeKey: string): boolean {
    const nowMs = Date.now();
    const lastLocalRefreshMs =
        activityUnreadCountLastRefetchByScope.get(scopeKey);
    if (
        lastLocalRefreshMs !== undefined &&
        nowMs - lastLocalRefreshMs <
            ACTIVITY_UNREAD_COUNT_REALTIME_THROTTLE_DELAY_MS
    ) {
        return false;
    }
    activityUnreadCountLastRefetchByScope.set(scopeKey, nowMs);

    if (typeof window === 'undefined') {
        return true;
    }

    try {
        const storageKey = `${ACTIVITY_UNREAD_COUNT_REFETCH_STORAGE_PREFIX}${scopeKey}`;
        const rawLastRefreshMs = window.localStorage.getItem(storageKey);
        const lastRefreshMs =
            rawLastRefreshMs === null ? null : Number(rawLastRefreshMs);
        if (
            lastRefreshMs !== null &&
            Number.isFinite(lastRefreshMs) &&
            nowMs - lastRefreshMs <
                ACTIVITY_UNREAD_COUNT_REALTIME_THROTTLE_DELAY_MS
        ) {
            return false;
        }
        window.localStorage.setItem(storageKey, String(nowMs));
    } catch {
        // Storage can be unavailable in private modes; the in-memory guard
        // still protects this tab.
    }

    return true;
}

function broadcastActivityUnreadCounts(
    scopeKey: string,
    counts: ActivityEventCountsResponse,
): void {
    if (typeof BroadcastChannel === 'undefined') {
        return;
    }

    const channel = new BroadcastChannel(
        ACTIVITY_UNREAD_COUNT_BROADCAST_CHANNEL,
    );
    channel.postMessage({
        type: 'activity-unread-counts-refreshed',
        scopeKey,
        counts,
    } satisfies ActivityUnreadCountBroadcastMessage);
    channel.close();
}

function useActivityUnreadCountBroadcast(
    scopeKey: string | null,
    mutate: KeyedMutator<ActivityEventCountsResponse>,
): void {
    useEffect(() => {
        if (!scopeKey || typeof BroadcastChannel === 'undefined') {
            return;
        }

        const channel = new BroadcastChannel(
            ACTIVITY_UNREAD_COUNT_BROADCAST_CHANNEL,
        );
        channel.onmessage = (event: MessageEvent<unknown>) => {
            if (!isActivityUnreadCountBroadcastMessage(event.data)) {
                return;
            }
            if (event.data.scopeKey !== scopeKey) {
                return;
            }
            void mutate(event.data.counts, { revalidate: false });
        };

        return () => {
            channel.close();
        };
    }, [mutate, scopeKey]);
}

function shallowEqual(
    a: Record<string, unknown> | undefined,
    b: Record<string, unknown> | undefined,
): boolean {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) {
        return false;
    }
    for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(b, key)) {
            return false;
        }
        if (!Object.is(a[key], b[key])) {
            return false;
        }
    }
    return true;
}

function mergeCollectionsById<T extends { id: string }>(
    current: ReadonlyArray<T> | undefined,
    incoming: ReadonlyArray<T>,
): { merged: Array<T>; changed: boolean } {
    if (!current || current.length === 0) {
        return {
            merged: [...incoming],
            changed: !current || current.length !== incoming.length,
        };
    }

    const currentMap = new Map(current.map((item) => [item.id, item]));
    let changed = current.length !== incoming.length;

    const merged = incoming.map((item) => {
        const existing = currentMap.get(item.id);
        if (
            existing &&
            shallowEqual(
                existing as Record<string, unknown>,
                item as unknown as Record<string, unknown>,
            )
        ) {
            return existing;
        }
        changed = true;
        return item;
    });

    return { merged, changed };
}

function extractResourceIds(
    event: OrganizationEvent,
    target: OrganizationEvent['resource']['type'],
): Array<string> {
    const ids = new Set<string>();

    if (event.resource.type === target) {
        ids.add(event.resource.id);
    }

    for (const relationship of event.relationships) {
        if (relationship.type === target) {
            ids.add(relationship.id);
        }
    }

    return Array.from(ids);
}

export function useOrganizationUnreadCountRealtime(
    reason: string,
    auto_update: boolean = true,
    organizationIdOverride?: string | null,
) {
    const defaultOrganizationId = useOrganizationId();
    const organizationId = organizationIdOverride ?? defaultOrganizationId;
    const { data, isLoading, error, isValidating, mutate } =
        useOrganizationUnreadCount(organizationId, { reason });

    const throttledMutate = useThrottledCallback(
        mutate,
        REALTIME_THROTTLE_DELAY_MS,
    );

    useListenForOrganizationEvent('conversations_unread_updated', () => {
        if (organizationId && auto_update) {
            throttledMutate();
        }
    });

    useListenForOrganizationEvent('conversations_mark_all_read', () => {
        if (organizationId && auto_update) {
            void mutate();
        }
    });

    return {
        data,
        isLoading,
        error,
        isValidating,
        mutate,
    };
}

export function useActivityUnreadCountRealtime(
    reason: string,
    auto_update: boolean = true,
    scope: string | null | ActivityUnreadCountRealtimeScope = {},
) {
    const defaultOrganizationId = useOrganizationId();
    const scopeOptions =
        typeof scope === 'object' && scope !== null
            ? scope
            : { organizationIdOverride: scope };
    const organizationId =
        scopeOptions.organizationIdOverride ?? defaultOrganizationId;
    const mode = useMode();
    const officeIds = normalizeScopeIds(scopeOptions.officeIds);
    const organizationFilterIds = normalizeScopeIds(
        scopeOptions.organizationFilterIds,
    );
    const careCoordinatorIds = normalizeScopeIds(
        scopeOptions.careCoordinatorIds,
    );
    const activityUnreadCountScopeKey = organizationId
        ? [
              organizationId,
              `mode=${mode}`,
              `offices=${scopeIdsKey(officeIds)}`,
              `organizations=${scopeIdsKey(organizationFilterIds)}`,
              `coordinators=${scopeIdsKey(careCoordinatorIds)}`,
          ].join(':')
        : null;
    const { data, isLoading, error, isValidating, mutate } =
        useActivityEventCounts(
            organizationId,
            officeIds,
            reason,
            {
                refreshIntervalMs: scopeOptions.refreshIntervalMs ?? null,
                revalidateOnFocus: scopeOptions.revalidateOnFocus ?? false,
            },
            organizationFilterIds,
            careCoordinatorIds,
        );
    useActivityUnreadCountBroadcast(activityUnreadCountScopeKey, mutate);

    const refreshActivityUnreadCount = useCallback(() => {
        if (!activityUnreadCountScopeKey) {
            return;
        }
        if (!claimActivityUnreadCountRefetch(activityUnreadCountScopeKey)) {
            return;
        }

        void mutate()
            .then((counts) => {
                if (counts === undefined) {
                    return;
                }
                broadcastActivityUnreadCounts(
                    activityUnreadCountScopeKey,
                    counts,
                );
            })
            .catch(() => undefined);
    }, [activityUnreadCountScopeKey, mutate]);

    const throttledMutate = useThrottledCallback(
        refreshActivityUnreadCount,
        ACTIVITY_UNREAD_COUNT_REALTIME_THROTTLE_DELAY_MS,
    );

    const refreshForActivityEvent = useCallback(
        (event: OrganizationEvent) => {
            if (
                !organizationId ||
                !auto_update ||
                !organizationEventReferencesResource(event, 'activity_event')
            ) {
                return;
            }
            throttledMutate();
        },
        [auto_update, organizationId, throttledMutate],
    );

    useListenForOrganizationEvent('notification_info', refreshForActivityEvent);
    useListenForOrganizationEvent(
        'notification_success',
        refreshForActivityEvent,
    );
    useListenForOrganizationEvent(
        'notification_warning',
        refreshForActivityEvent,
    );
    useListenForOrganizationEvent(
        'notification_error',
        refreshForActivityEvent,
    );
    useListenForOrganizationEvent(
        'activity_event_created',
        refreshForActivityEvent,
    );
    useListenForOrganizationEvent('notification_state_updated', () => {
        if (organizationId && auto_update) {
            throttledMutate();
        }
    });

    return {
        data: data?.action_required,
        isLoading,
        error,
        isValidating,
        mutate,
    };
}

export function useOrganizationConversationRealtime(
    conversationId: string | null,
    auto_update: boolean = true,
    markAsRead: boolean = true,
    organizationIdOverride?: string | null,
    autoLoadPages: boolean = false,
) {
    const defaultOrganizationId = useOrganizationId();
    const organizationId = organizationIdOverride ?? defaultOrganizationId;
    const {
        conversation,
        messages,
        pages,
        isLoading,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        error,
        isValidating,
        mutate,
        revalidateFirstPage,
    } = useOrganizationConversation(
        organizationId,
        conversationId ?? null,
        { reason: 'use-organization-conversation-realtime' },
        {
            autoLoadPages,
            markAsRead,
        },
    );

    // Throttle conversation refreshes to one request per second.
    const throttledMutate = useThrottledCallback(mutate, 1000);
    const throttledRevalidateFirstPage = useThrottledCallback(
        revalidateFirstPage,
        1000,
    );

    useListenForOrganizationEvent('conversations_unread_updated', (event) => {
        if (!conversationId || !organizationId || !auto_update) {
            return;
        }
        if (event.resource.id !== conversationId) {
            return;
        }
        throttledMutate();
    });

    useListenForOrganizationEvent('conversation_updated', (event) => {
        if (!conversationId || !organizationId || !auto_update) {
            return;
        }
        if (event.resource.id !== conversationId) {
            return;
        }
        throttledMutate();
    });

    useListenForOrganizationEvent('message_created', (event) => {
        if (!conversationId || !organizationId || !auto_update) {
            return;
        }
        event.relationships.forEach((relationship) => {
            if (
                relationship.type === 'conversation' &&
                relationship.id === conversationId
            ) {
                throttledRevalidateFirstPage();
            }
        });
    });

    useListenForOrganizationEvent('pending_message_updated', (event) => {
        if (!conversationId || !organizationId || !auto_update) {
            return;
        }

        const conversationRef = event.relationships.find(
            (relationship) => relationship.type === 'conversation',
        );
        if (!conversationRef || conversationRef.id !== conversationId) {
            return;
        }

        throttledMutate();
    });

    useListenForOrganizationEvent('voice_call_updated', (event) => {
        if (!conversationId || !organizationId || !auto_update) {
            return;
        }
        if (event.resource.type !== 'voice_call') {
            return;
        }

        const hasUpdatedCall = (conversation?.calls ?? []).some(
            (call) => call.id === event.resource.id,
        );
        if (!hasUpdatedCall) {
            return;
        }

        throttledMutate();
    });

    // The note body otherwise rides on the best-effort, at-most-once
    // `conversation_updated` broadcast: if that single frame is dropped, the
    // note stays hidden until a manual refresh even though its mention
    // notification (delivered reliably to the tagged user) already arrived.
    // Revalidate off that reliable signal so the note appears in step with its
    // notification.
    useListenForOrganizationEvent('notification_info', (event) => {
        if (!conversationId || !organizationId || !auto_update) {
            return;
        }
        if (
            event.resource.type !== 'conversation' ||
            event.resource.id !== conversationId
        ) {
            return;
        }
        throttledMutate();
    });

    return {
        conversation,
        messages,
        isLoading,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        isValidating,
        error,
        pages,
        mutate,
    };
}

export function useOrganizationConversationsRealtime({
    organizationId: organizationIdOverride,
    search,
    caregiverId,
    hasPendingMessages,
    sort,
    includeHidden,
    readState,
    officeIds,
    includeUnassignedOffice,
    status,
    assignee,
    pageSize,
    enabled,
}: {
    organizationId?: string | null;
    search?: string;
    caregiverId?: string;
    hasPendingMessages?: boolean;
    sort?: 'pending_first';
    includeHidden?: boolean;
    readState?: 'unread' | 'read';
    officeIds?: ReadonlyArray<string>;
    includeUnassignedOffice?: boolean;
    status?: 'open' | 'closed';
    assignee?: string;
    pageSize?: number;
    enabled?: boolean;
}) {
    const defaultOrganizationId = useOrganizationId();
    const organizationId = organizationIdOverride ?? defaultOrganizationId;
    const {
        conversations,
        isLoading,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        error,
        isValidating,
        mutate,
    } = useOrganizationConversations(
        organizationId,
        { reason: 'use-organization-conversations-realtime' },
        search,
        {
            caregiverId,
            hasPendingMessages,
            sort,
            includeHidden,
            readState,
            officeIds,
            includeUnassignedOffice,
            status,
            assignee,
            pageSize,
            enabled,
        },
    );
    const throttledRevalidateList = useThrottledCallback(() => {
        void mutate();
    }, REALTIME_THROTTLE_DELAY_MS);
    useOrganizationReconnectRecovery({
        organizationId,
        recoveryLabel: 'conversations-list',
        priority: 10,
        recover: mutate,
        enabled,
    });

    // Staleness floor. Live events are the fast path, but Redis pub/sub is
    // at-most-once: a failover or a dropped publish loses an event outright
    // while the socket stays healthy, so neither the liveness watchdog nor the
    // reconnect path can recover it. Goes through `mutate()` rather than SWR's
    // `refreshInterval` so every loaded page refreshes together, and only runs
    // while the tab is visible.
    useEffect(() => {
        if (!organizationId || typeof document === 'undefined') {
            return;
        }

        const intervalId = setInterval(() => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            throttledRevalidateList();
        }, CONVERSATIONS_LIST_STALENESS_FLOOR_MS);

        return () => {
            clearInterval(intervalId);
        };
    }, [organizationId, throttledRevalidateList]);

    useEffect(() => {
        if (
            !organizationId ||
            typeof window === 'undefined' ||
            typeof document === 'undefined'
        ) {
            return;
        }

        const revalidateIfVisible = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            void mutate();
        };

        window.addEventListener('focus', revalidateIfVisible);
        document.addEventListener('visibilitychange', revalidateIfVisible);

        return () => {
            window.removeEventListener('focus', revalidateIfVisible);
            document.removeEventListener(
                'visibilitychange',
                revalidateIfVisible,
            );
        };
    }, [mutate, organizationId]);

    useListenForOrganizationEvent('conversations_unread_updated', (event) => {
        if (!organizationId) {
            return;
        }

        if (event.resource.type !== 'conversation') {
            return;
        }

        // Unread changes can move a conversation into or out of filtered
        // views. Refetch the ordered pages so a conversation missing from the
        // local cache can be restored instead of leaving this browser stale.
        throttledRevalidateList();
    });

    useListenForOrganizationEvent('conversations_mark_all_read', () => {
        if (!organizationId) {
            return;
        }

        void mutate();
    });

    useListenForOrganizationEvent('conversation_updated', (event) => {
        if (!organizationId) {
            return;
        }

        if (event.resource.type !== 'conversation') {
            return;
        }

        throttledRevalidateList();
    });

    useListenForOrganizationEvent('message_created', (event) => {
        if (!organizationId) {
            return;
        }

        const conversationRef = event.relationships.find(
            (relationship) => relationship.type === 'conversation',
        );

        if (!conversationRef) {
            return;
        }

        throttledRevalidateList();
    });

    useListenForOrganizationEvent('pending_message_updated', () => {
        if (!organizationId) {
            return;
        }

        throttledRevalidateList();
    });

    return {
        conversations,
        isLoading,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        isValidating,
        error,
        mutate,
    };
}

export function useOrganizationPendingMessagesRealtime(
    reason: string,
    disabled: boolean = false,
    options?: {
        organizationId?: string | null;
        autoUpdate?: boolean;
        conversationIdFilter?: string | Array<string> | null;
        domain?: StagedMessageDomain;
        domainId?: string | null;
        conversationId?: string | null;
        careCoordinatorIds?: Array<string>;
        includeUnassignedCareCoordinators?: boolean;
    },
) {
    const defaultOrganizationId = useOrganizationId();
    const organizationId = options?.organizationId ?? defaultOrganizationId;
    const autoUpdate = options?.autoUpdate ?? true;
    const conversationFilter = options?.conversationIdFilter;
    const conversationFilterSet = useMemo<Set<string> | null>(() => {
        if (!conversationFilter) {
            return null;
        }
        return new Set(
            Array.isArray(conversationFilter)
                ? conversationFilter.filter(Boolean)
                : [conversationFilter].filter(Boolean),
        ) as Set<string>;
    }, [conversationFilter]);
    const domain = options?.domain;
    const domainId = options?.domainId ?? undefined;
    const conversationId = options?.conversationId ?? undefined;
    const careCoordinatorIds = options?.careCoordinatorIds;
    const includeUnassignedCareCoordinators =
        options?.includeUnassignedCareCoordinators;
    const {
        data,
        isLoading,
        error,
        isValidating,
        mutate: revalidate,
    } = useOrganizationPendingMessages(
        !disabled ? organizationId : null,
        {
            reason,
        },
        {
            ...(conversationId && { conversationId }),
            ...(domain && { domain }),
            ...(domainId && { domainId }),
            ...(careCoordinatorIds &&
                careCoordinatorIds.length > 0 && { careCoordinatorIds }),
            ...(includeUnassignedCareCoordinators && {
                includeUnassignedCareCoordinators,
            }),
        },
    );

    const areMessagesEqualById = useCallback(
        (a: Array<StagedMessage>, b: Array<StagedMessage>): boolean => {
            if (a === b) {
                return true;
            }

            if (a.length !== b.length) {
                return false;
            }

            return a.every((message, index) => message.id === b[index]?.id);
        },
        [],
    );

    const matchesFilters = useCallback(
        (message: StagedMessage): boolean => {
            const matchesConversation = conversationId
                ? message.conversation_id === conversationId
                : conversationFilterSet
                  ? message.conversation_id
                      ? conversationFilterSet.has(message.conversation_id)
                      : false
                  : true;

            const matchesDomain = domain
                ? message.domain === domain &&
                  (!domainId || message.domain_id === domainId)
                : true;

            return matchesConversation && matchesDomain;
        },
        [conversationFilterSet, conversationId, domain, domainId],
    );

    const filterMessagesForConversations = useCallback(
        (messages: Array<StagedMessage>): Array<StagedMessage> => {
            return messages.filter(matchesFilters);
        },
        [matchesFilters],
    );

    const [pendingMessages, setPendingMessages] = useState<
        Array<StagedMessage>
    >(() => (data ? filterMessagesForConversations(data) : []));

    useEffect(() => {
        if (data !== undefined) {
            const nextMessages = filterMessagesForConversations(data);
            setPendingMessages((current) =>
                areMessagesEqualById(current, nextMessages)
                    ? current
                    : nextMessages,
            );
        }
    }, [data, filterMessagesForConversations, areMessagesEqualById]);

    useEffect(() => {
        setPendingMessages((current: Array<StagedMessage>) => {
            const nextMessages = filterMessagesForConversations(current);
            return areMessagesEqualById(current, nextMessages)
                ? current
                : nextMessages;
        });
    }, [filterMessagesForConversations, areMessagesEqualById]);

    const handlePendingMessageUpdated = useCallback(
        async (event: OrganizationEvent) => {
            if (!organizationId || !autoUpdate) {
                return;
            }

            if (event.resource.type !== 'staged_message') {
                return;
            }

            const pendingMessageId = event.resource.id;
            if (!pendingMessageId) {
                return;
            }

            try {
                const latest = await getApiClient().get({
                    url: '/organizations/{organization_id}/pending-messages/{pending_message_id}',
                    pathParams: {
                        organization_id: organizationId,
                        pending_message_id: pendingMessageId,
                    },
                });
                setPendingMessages((current: Array<StagedMessage>) => {
                    const stagedMessages = current;
                    const index = stagedMessages.findIndex(
                        (message) => message.id === latest.id,
                    );

                    const isRelevant = matchesFilters(latest);

                    if (!isRelevant) {
                        if (index === -1) {
                            return stagedMessages;
                        }
                        return [
                            ...stagedMessages.slice(0, index),
                            ...stagedMessages.slice(index + 1),
                        ];
                    }

                    if (latest.status !== 'pending') {
                        if (index === -1) {
                            return stagedMessages;
                        }
                        return [
                            ...stagedMessages.slice(0, index),
                            ...stagedMessages.slice(index + 1),
                        ];
                    }

                    const updatedMessages: Array<StagedMessage> =
                        index === -1
                            ? [latest, ...stagedMessages]
                            : stagedMessages.map((message, idx) =>
                                  idx === index ? latest : message,
                              );

                    return [...updatedMessages].sort(
                        (a, b) =>
                            new Date(b.created_at).getTime() -
                            new Date(a.created_at).getTime(),
                    );
                });
            } catch (error_) {
                setPendingMessages((current: Array<StagedMessage>) =>
                    current.filter(
                        (message) => message.id !== pendingMessageId,
                    ),
                );
                console.error(
                    '[useOrganizationPendingMessagesRealtime] Failed to load pending message after event',
                    {
                        pendingMessageId,
                        event,
                        error: error_,
                    },
                );
            }
        },
        [organizationId, autoUpdate, matchesFilters],
    );

    const throttledHandlePendingMessageUpdated = useThrottledCallback(
        handlePendingMessageUpdated,
        REALTIME_THROTTLE_DELAY_MS,
    );

    useListenForOrganizationEvent('pending_message_updated', (event) => {
        void throttledHandlePendingMessageUpdated(event);
    });

    return {
        data: pendingMessages,
        isLoading: isLoading && pendingMessages.length === 0,
        error,
        isValidating,
        mutate: revalidate,
    };
}

export function useOrganizationPendingMessagesCountRealtime(
    reason: string,
    options?: {
        organizationId?: string | null;
        autoUpdate?: boolean;
        conversationId?: string | null;
        domain?: StagedMessageDomain;
        domainId?: string | null;
        careCoordinatorIds?: Array<string>;
        includeUnassignedCareCoordinators?: boolean;
    },
) {
    const { organizationId: overrideOrganizationId, autoUpdate = true } =
        options ?? {};
    const defaultOrganizationId = useOrganizationId();
    const organizationId = overrideOrganizationId ?? defaultOrganizationId;
    const { data, isLoading, error, isValidating, mutate } =
        useOrganizationPendingMessagesCount(
            organizationId ?? '',
            { reason },
            {
                conversationId: options?.conversationId ?? undefined,
                domain: options?.domain ?? undefined,
                domainId: options?.domainId ?? undefined,
                careCoordinatorIds: options?.careCoordinatorIds ?? undefined,
                includeUnassignedCareCoordinators:
                    options?.includeUnassignedCareCoordinators ?? undefined,
            },
        );

    const triggerRefresh = useCallback(() => {
        if (!organizationId || !autoUpdate) {
            return;
        }

        void mutate();
    }, [autoUpdate, mutate, organizationId]);

    const throttledTriggerRefresh = useThrottledCallback(
        triggerRefresh,
        REALTIME_THROTTLE_DELAY_MS,
    );

    useListenForOrganizationEvent('pending_message_updated', (event) => {
        if (event.resource.type !== 'staged_message') {
            return;
        }
        throttledTriggerRefresh();
    });

    return {
        data,
        isLoading,
        error,
        isValidating,
        mutate,
    };
}

export function useContactAttemptsRealtime(
    outreachId: string | null,
    reason: string,
    auto_update: boolean = true,
) {
    const organizationId = useOrganizationId();
    const { data, isLoading, error, isValidating, mutate } = useContactAttempts(
        organizationId,
        outreachId,
        { reason },
    );

    const dataRef = useRef<ReadonlyArray<ContactAttempt> | undefined>(data);
    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const refreshContactAttempts = useCallback(
        async (contactAttemptIds?: Array<string>) => {
            if (!organizationId || !outreachId || !auto_update) {
                return;
            }

            try {
                const latest = await getApiClient().get({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/contact-attempts',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreachId,
                    },
                });

                if (!Array.isArray(latest)) {
                    return;
                }

                const latestList = latest as Array<ContactAttempt>;
                const current = dataRef.current;
                const { merged, changed } = mergeCollectionsById(
                    current,
                    latestList,
                );

                if (!changed) {
                    return;
                }

                await mutate(merged, { revalidate: false });
            } catch (error_) {
                console.error(
                    '[useContactAttemptsRealtime] Failed to refresh contact attempts',
                    { outreachId, contactAttemptIds, error: error_ },
                );
            }
        },
        [auto_update, outreachId, mutate, organizationId],
    );

    const throttledRefreshContactAttempts = useThrottledCallback(
        refreshContactAttempts,
        CONTACT_ATTEMPTS_THROTTLE_DELAY,
    );

    useListenForOrganizationEvent('contact_attempt_updated', (event) => {
        if (!organizationId || !outreachId || !auto_update) {
            return;
        }

        const isCurrentOutreach = extractResourceIds(event, 'callout').some(
            (id) => id === outreachId,
        );

        if (!isCurrentOutreach) {
            return;
        }

        const attemptIds = extractResourceIds(event, 'contact_attempt');
        void throttledRefreshContactAttempts(attemptIds);
    });

    return {
        data,
        isLoading,
        error,
        isValidating,
        mutate,
    };
}

export function useOutreachRelatedInboundCallsRealtime(
    outreachId: string | null,
    reason: string,
    auto_update: boolean = true,
) {
    const organizationId = useOrganizationId();
    const { data, isLoading, error, isValidating, mutate } =
        useOutreachRelatedInboundCalls(organizationId, outreachId, {
            reason,
        });

    const refreshRelatedInboundCalls = useCallback(async () => {
        if (!organizationId || !outreachId || !auto_update) {
            return;
        }

        await mutate();
    }, [auto_update, outreachId, mutate, organizationId]);

    const throttledRefreshRelatedInboundCalls = useThrottledCallback(
        refreshRelatedInboundCalls,
        CONTACT_ATTEMPTS_THROTTLE_DELAY,
    );

    useListenForOrganizationEvent('contact_attempt_updated', (event) => {
        if (!organizationId || !outreachId || !auto_update) {
            return;
        }

        const isCurrentOutreach = extractResourceIds(event, 'callout').some(
            (id) => id === outreachId,
        );
        if (!isCurrentOutreach) {
            return;
        }

        void throttledRefreshRelatedInboundCalls();
    });

    useListenForOrganizationEvent('voice_call_updated', (event) => {
        if (!organizationId || !outreachId || !auto_update) {
            return;
        }

        const isCurrentOutreach = extractResourceIds(event, 'callout').some(
            (id) => id === outreachId,
        );
        if (!isCurrentOutreach) {
            return;
        }

        void throttledRefreshRelatedInboundCalls();
    });

    return {
        data,
        isLoading,
        error,
        isValidating,
        mutate,
    };
}

export function useClientContactAttemptsRealtime(
    outreachId: string | null,
    reason: string,
    auto_update: boolean = true,
) {
    const organizationId = useOrganizationId();
    const { data, isLoading, error, isValidating, mutate } =
        useClientContactAttempts(organizationId, outreachId, {
            reason,
        });

    const dataRef = useRef(data);
    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const refreshClientContactAttempts = useCallback(
        async (contactAttemptIds?: Array<string>) => {
            if (!organizationId || !outreachId || !auto_update) {
                return;
            }

            try {
                const latest = await getApiClient().get({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/client-contact-attempts',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreachId,
                    },
                });

                if (!Array.isArray(latest)) {
                    return;
                }

                const latestList = latest as Array<{ id: string }>;
                const current = dataRef.current as
                    | ReadonlyArray<{ id: string }>
                    | undefined;
                const { merged, changed } = mergeCollectionsById(
                    current,
                    latestList,
                );

                if (!changed) {
                    return;
                }

                await mutate(merged as typeof data, { revalidate: false });
            } catch (error_) {
                console.error(
                    '[useClientContactAttemptsRealtime] Failed to refresh client contact attempts',
                    { outreachId, contactAttemptIds, error: error_ },
                );
            }
        },
        [auto_update, outreachId, mutate, organizationId],
    );

    const throttledRefreshClientContactAttempts = useThrottledCallback(
        refreshClientContactAttempts,
        CONTACT_ATTEMPTS_THROTTLE_DELAY,
    );

    useListenForOrganizationEvent('contact_attempt_updated', (event) => {
        if (!organizationId || !outreachId || !auto_update) {
            return;
        }

        const isCurrentOutreach = extractResourceIds(event, 'callout').some(
            (id) => id === outreachId,
        );

        if (!isCurrentOutreach) {
            return;
        }

        const attemptIds = extractResourceIds(event, 'contact_attempt');
        void throttledRefreshClientContactAttempts(attemptIds);
    });

    return {
        data,
        isLoading,
        error,
        isValidating,
        mutate,
    };
}

type ShiftsResponse = ReturnType<typeof useShifts>['data'];

/** Keeps the displayed calendar range current after shift event bursts. */
export function useCalendarShiftsInRangeRealtime(
    ...parameters: Parameters<typeof useCalendarShiftsInRange>
) {
    const [organizationId] = parameters;
    const result = useCalendarShiftsInRange(...parameters);
    const { mutate } = result;

    const refreshShifts = useCallback(async () => {
        if (!organizationId) {
            return;
        }

        await mutate();
    }, [mutate, organizationId]);

    const throttledRefreshShifts = useThrottledCallback(
        refreshShifts,
        REALTIME_THROTTLE_DELAY_MS,
    );

    useListenForOrganizationEvent(
        'shift_confirmation_updated',
        throttledRefreshShifts,
        { organizationId },
    );
    useListenForOrganizationEvent('shifts_updated', throttledRefreshShifts, {
        organizationId,
    });
    useOrganizationReconnectRecovery({
        organizationId,
        recoveryLabel: 'calendar-shifts',
        priority: 30,
        recover: refreshShifts,
    });

    return result;
}

export function useShiftsRealtime(
    reason: string,
    params?: Parameters<typeof useShifts>[2],
    options?: Parameters<typeof useShifts>[3],
    auto_update: boolean = true,
) {
    const organizationId = useOrganizationId();
    const { data, isLoading, error, isValidating, mutate } = useShifts(
        organizationId,
        { reason },
        params,
        options,
    );

    const dataRef = useRef<ShiftsResponse | undefined>(data);
    useEffect(() => {
        dataRef.current = data;
    }, [data]);

    const refreshShifts = useCallback(async () => {
        if (!organizationId || !auto_update) {
            return;
        }

        await mutate();
    }, [auto_update, mutate, organizationId]);

    const throttledRefreshShifts = useThrottledCallback(
        refreshShifts,
        REALTIME_THROTTLE_DELAY_MS,
    );

    const refreshByConversations = useCallback(
        (conversationIds: Array<string>) => {
            const current = dataRef.current;
            if (!current?.shifts) {
                return;
            }

            const hasRelatedShift = conversationIds.some((conversationId) =>
                current.shifts.some(
                    (shift) => shift.conversation_id === conversationId,
                ),
            );

            if (!hasRelatedShift) {
                return;
            }

            void throttledRefreshShifts();
        },
        [throttledRefreshShifts],
    );

    useListenForOrganizationEvent('shift_confirmation_updated', () => {
        if (!organizationId || !auto_update) {
            return;
        }

        void throttledRefreshShifts();
    });

    useListenForOrganizationEvent('shifts_updated', () => {
        if (!organizationId || !auto_update) {
            return;
        }

        void throttledRefreshShifts();
    });

    useListenForOrganizationEvent('conversations_unread_updated', (event) => {
        if (!auto_update) {
            return;
        }

        const conversationIds = extractResourceIds(event, 'conversation');
        if (conversationIds.length === 0) {
            return;
        }

        refreshByConversations(conversationIds);
    });

    useListenForOrganizationEvent('conversation_updated', (event) => {
        if (!auto_update) {
            return;
        }

        const conversationIds = extractResourceIds(event, 'conversation');
        if (conversationIds.length === 0) {
            return;
        }

        refreshByConversations(conversationIds);
    });

    useListenForOrganizationEvent('message_created', (event) => {
        if (!auto_update) {
            return;
        }

        const conversationIds = extractResourceIds(event, 'conversation');
        if (conversationIds.length === 0) {
            return;
        }

        refreshByConversations(conversationIds);
    });

    useListenForOrganizationEvent('pending_message_updated', (event) => {
        if (!auto_update) {
            return;
        }

        const conversationIds = extractResourceIds(event, 'conversation');
        if (conversationIds.length === 0) {
            return;
        }

        refreshByConversations(conversationIds);
    });

    return {
        data,
        isLoading,
        error,
        isValidating,
        mutate,
    };
}
